import React, { useCallback, useEffect, useRef } from 'react';

import { EDITOR_PLAYER_ID, isAllowAutoPlay, joinClassNames, STORY_CONFIG_ID } from '../../util/util';
import { CHANGE_SCENE_TYPES, PLAYER_TRANSITIONS_OUT } from '../../util/constants';
import { findHighestQualityVideo } from '../../util/media-data-util';
import { LiveHelper } from '../../util/live-helper';
import { NumberHelper } from '../../util/number-helper';
import { MessageHelper } from '../../util/message-helper';
import { sendMediaGAPauseData, sendMediaGAPlayData, sendMediaGAViewData } from '../../util/gAUtil';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { usePlayer } from '../../contexts/PlayerContext';
import { useChatLogin } from '../../contexts/ChatLoginContext';
import { usePresenter } from '../../contexts/PresenterContext';

import { getSceneEndExitDuration } from '../Interact/InteractOverlay/WidgetElement';

import { PlayerHelper } from './player-helper';
import { FetchDataHelper } from './hooks/fetch-data-helper';

import './Player.scss';

const isStoryPlaylistConfig = (playlistId: string | undefined, configParams: any) =>
    !!playlistId && typeof configParams?.configId === "string" && configParams.configId.toLowerCase().includes(STORY_CONFIG_ID.toLowerCase());

const Player = (props: PlayerHelper.PlayerProps) => {
    const { playerId, isVisible, token, playerMedia, settings, classNames, handlePlayerHover, isMaintainProportions, mediaData, layout } = props;

    const createdPlayerInstance = React.useRef(false);
    const checkpointRef = React.useRef(true);
    const positionMessage = React.useRef('no-notify');
    const liveStoppingTime = React.useRef<number | null>(null);
    const hasTriggeredTransitionEffect = React.useRef(false);
    // Start time from `?id=<mediaId>&in=<seconds>` / data-qplayer-startAt: undefined until read on the
    // first position event, null once applied or not wanted.
    const pendingInTime = React.useRef<number | null | undefined>(undefined);
    const inTimeSeekAttempts = React.useRef(0);

    const [loading, setLoading] = React.useState(true);
    const [isSendViewGAEvent, setIsSendViewGAEvent] = React.useState(false);

    const { currentPosition, currentSceneWidgets, duration, mediaDurations, isPlaying, currentVolumeLevel, isPosterRemoved,
        startPlayerAt,
        setStartPlayerAt,
        setPlayerReady,
        setPlayerCurrentPosition,
        setPlayerDuration,
        setRemovePoster,
        setShowChatWindow,
        setAdsPlaying, setCurrentInteractiveElement, setCurrentVolumeLevel, setIsLiveStarted, setIsLiveStopped, setIsPlaying, setMediaWithDuration
    } = usePlayer();
    const { setShowMobileChat } = useChatLogin();
    const { addPresentationPublishedSlide } = usePresenter();

    const { isCloseIncomingMessage, viewerEngagementModuleActivated, isChatActivated, mediaName, accountId, isMediaLive, sceneConfig, mediasData, playlistId, mediaType, thumbnailUrl, scenario, shouldChangeCurrentMedia, changeSceneType, isTransitionLayerEnable, showCountdownModule, currentMediaId, restrictedMedias, minimizedPlayer, configParams,
        setChangeSceneType, setChapters, setChatActivated, setCloseIncomingMessage, setCurrentMediaId, setIsMediaLive, setMediasData, setSceneConfig, setSceneHistory, setRestrictedMedias, setTransitionEffectStatus, setTransitionLayer, setSceneEndExiting, setTransitionOut, setVideoDownloadUrl, setVideoHoverUrl, setViewerEngagementModuleActivated, updateSceneHistory, setIsMaintainProportions, setLiveTranscriptLanguage, setEnabledLiveTranscript, setUserSeekedPlayer, errorDetails, setErrorDetails
    } = useConfiguration();

    const getStateRef = useCallback(() => ({
        props: props,
        isMediaLive: isMediaLive,
        isPlaying: isPlaying,
        duration: duration,
        mediaType: mediaType,
        mediasData: mediasData,
        sceneConfig: sceneConfig,
        scenario: scenario,
        isTransitionLayerEnable: isTransitionLayerEnable,
        currentPosition: currentPosition,
        currentSceneWidgets: currentSceneWidgets,
        startPlayerAt: startPlayerAt,
        mediaName: mediaName,
        accountId: accountId,
        shouldChangeCurrentMedia: shouldChangeCurrentMedia,
        changeSceneType: changeSceneType,
        currentMediaId: currentMediaId,
        currentVolumeLevel: currentVolumeLevel,
        isPosterRemoved: isPosterRemoved,
        mediaDurations: mediaDurations,
        playlistId: playlistId,
        restrictedMedias: restrictedMedias,
        configParams: configParams,
        errorDetails: errorDetails
    }), [startPlayerAt, accountId, changeSceneType, currentMediaId, currentPosition, currentSceneWidgets, currentVolumeLevel, duration, isMediaLive, isPlaying, isPosterRemoved, isTransitionLayerEnable, mediaDurations, mediaName, mediaType, mediasData, playlistId, props, scenario, sceneConfig, shouldChangeCurrentMedia, restrictedMedias, configParams, errorDetails]);

    // Live duration, kept in a ref so it is readable in the SAME tick it arrives.
    //
    // For live, `currentPosition` is `duration + position`: `duration` is the length of the
    // seekable window and `position` is a negative offset back from the live edge. GoBrain emits
    // both from one `timeupdate` - `durationChanged` first, then `positionChanged` with a freshly
    // read live edge (media-players/video-js-8.js). `latestState.current` however is only
    // refreshed in an effect, i.e. after React commits a render, so the position handler used to
    // add a fresh position to the PREVIOUS duration. Every time the stream appended a segment the
    // combined value therefore dipped backwards by about that segment and snapped forward again a
    // tick later, which made position-driven UI (the presenter's active slide most visibly) jump
    // back and forth. Reading the duration from a ref removes the skew at the source.
    const latestLiveDuration = useRef(0);

    const latestState = useRef(getStateRef());
    useEffect(() => {
        latestState.current = getStateRef();
    }, [props, isMediaLive, mediaType, isPlaying, duration, mediasData, sceneConfig, scenario, isTransitionLayerEnable, currentPosition, currentSceneWidgets, mediaName, accountId, shouldChangeCurrentMedia, changeSceneType, currentMediaId, currentVolumeLevel, isPosterRemoved, mediaDurations, playlistId, getStateRef]);

    useEffect(() => {
        if (!isTransitionLayerEnable) {
            hasTriggeredTransitionEffect.current = false;
        }
    }, [isTransitionLayerEnable]);

    const onPlayerReady = useCallback((value) => setPlayerReady(value), []);

    const hideControls = useCallback(() => {
        (window as any).GoBrain.widgets()[playerId].pause();
        setIsPlaying(false);
        setAdsPlaying(true);
    }, [playerId]);

    const showControls = useCallback(() => {
        setAdsPlaying(false);
        setIsPlaying(true);
    }, []);

    const turnOfChat = useCallback(() => {
        setViewerEngagementModuleActivated(false);
        setChatActivated(false);
        setCloseIncomingMessage(true);
    }, []);

    const updateChatState = useCallback((mediaDataCustom: any) => {
        const shouldActivateViewerEngagementModule = mediaDataCustom?.activatedViewerEngagement && !mediaDataCustom?.hideEngagementModule;
        if (shouldActivateViewerEngagementModule !== viewerEngagementModuleActivated) {
            setViewerEngagementModuleActivated(shouldActivateViewerEngagementModule);
        }

        const shouldActivateChat = mediaDataCustom?.activatedChat !== false;
        if (shouldActivateChat !== isChatActivated) {
            setChatActivated(shouldActivateChat); // default is true which means "undefined" should be true
        }

        if (mediaDataCustom?.closeIncomingMessage !== isCloseIncomingMessage) {
            setCloseIncomingMessage(!!mediaDataCustom?.closeIncomingMessage);
        }
    }, [isChatActivated, isCloseIncomingMessage, viewerEngagementModuleActivated]);

    const onPlayerQueueItem = useCallback(({ currentItem, currentIndex }: any) => {
        const { scenario, restrictedMedias, changeSceneType, currentMediaId, currentVolumeLevel, mediasData, shouldChangeCurrentMedia, isMediaLive, props, configParams } = latestState.current;
        const { playerId, isVisible, token, playerMedia, isMaintainProportions } = props;

        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.queueItem,
            mediaId: playerMedia,
            scenarioId: scenario?.id,
            metadata: { queueItem: { currentItem, currentIndex } },
            qplayerId: configParams.qplayerId
        });

        if (!currentItem && !currentIndex) {
            createdPlayerInstance.current = false;
            return;
        }

        if (currentItem?.playlistId && currentItem.resources?.[0]) {
            currentItem.resources[0].live !== isMediaLive && setIsMediaLive(currentItem.resources[0].live);
            setCurrentMediaId(currentItem.id);
        }

        const mediaData = currentItem ? FetchDataHelper.getMediaData({
            currentIndex: currentIndex,
            mediaData: currentItem,
            id: currentItem?.id ?? playerMedia,
            token,
            saveInteractiveElementToStore: (elems: any[]) => setCurrentInteractiveElement(elems)
        }) : undefined;

        if (mediaData && !currentItem.playlistId) {
            const existingMediaData = mediasData.find((m: any) => m.id === playerMedia);
            // `{ ...mediaData, ...m }` means the existing entry always wins on shared keys,
            // so the merge only ever changes anything when mediaData has a key existingMediaData lacks.
            // Each entry is merged on its own: a scenario media can have one entry per scene (own sceneId/endOfScene).
            const hasMediaDataChanged = !existingMediaData || Object.keys(mediaData).some((key) => !(key in existingMediaData));
            if (hasMediaDataChanged) {
                const newMediasData = mediasData.length ? mediasData.map((m: any) => (m.id === playerMedia ? { ...mediaData, ...m } : m)) : [mediaData];
                setMediasData(newMediasData);
            }
            mediaData.live && mediaData.id === currentMediaId && !isMediaLive && setIsMediaLive(true);
        }

        // onPlayerError flags the media under `playerMedia` (the requested id), but the queue item
        // can carry a different id - e.g. a live media that references its recording. Clear both,
        // or the password screen stays up over a video that is already playing.
        const unlockedIds = [currentItem?.id, playerMedia];
        if (restrictedMedias.some((id: string) => unlockedIds.includes(id))) {
            const newRestrictedMedias = restrictedMedias.filter((id: string) => !unlockedIds.includes(id));
            setRestrictedMedias(newRestrictedMedias);
        }

        if ((currentItem.resources ?? [])[0].streams.length === 0) {
            const newError = { details: { type: PlayerHelper.ERROR_TYPES.VideoCorrupted }, mediaId: currentItem.id };
            const newErrorDetails = errorDetails.filter((error: any) => error.mediaId !== playerMedia).filter(Boolean);
            newErrorDetails.push(newError);
            setErrorDetails(newErrorDetails);
            return;
        }

        if (!isVisible) {
            return;
        }

        if (currentItem?.id) {
            updateChatState(mediaData?.custom ?? {});
            if (mediaData?.live) {
                if (currentItem?.custom?.livemanager) {
                    const isLiveStarted = !!currentItem?.custom?.livemanager?.start && !currentItem?.custom?.livemanager?.stop;
                    const isLiveStopped = !!currentItem?.custom?.livemanager?.start && !!currentItem?.custom?.livemanager?.stop
                        ? currentItem?.custom?.livemanager?.stop
                        : false;
                    const liveTranscriptLanguage = currentItem?.custom?.livemanager?.liveTranscriptLanguage || '';
                    const enabledLiveTranscript = currentItem?.custom?.livemanager?.enabledLiveTranscript === undefined
                        ? false
                        : currentItem?.custom?.livemanager?.enabledLiveTranscript;

                    if ((!isLiveStarted || isLiveStopped) && currentItem.playlistId) {
                        (window as any).GoBrain.widgets()[playerId].pause();
                        turnOfChat();
                        setIsPlaying(undefined);
                    }

                    setIsLiveStarted(isLiveStarted);
                    setIsLiveStopped(isLiveStopped);
                    setLiveTranscriptLanguage(liveTranscriptLanguage);
                    setEnabledLiveTranscript(enabledLiveTranscript);
                } else {
                    turnOfChat();
                    setIsLiveStarted(true);
                    setIsLiveStopped(false);
                }
            }

            const downloadUrl = findHighestQualityVideo(currentItem.resources?.[0]?.downloadables);
            const previewUrl = ((currentItem.resources ?? [])[0].downloadables ?? []).find(({ label }: { label: any }) => label === 'Low')?.url;
            setVideoDownloadUrl(downloadUrl ?? '');
            setVideoHoverUrl(previewUrl ?? '');

            const chapters = currentItem.resources?.[0]?.tracks?.find((track: any) => track.type === 'chapter')?.sequences ?? [];
            setChapters(chapters)

            const videoStream = (((currentItem.resources ?? [])[0].streams ?? [])[0].videos ?? []).find((v: any) => v.height && v.width);
            const isMaintainProportionsValue = videoStream && (Math.floor(videoStream.width / videoStream.height * 1000) / 1000) === (Math.floor(9 / 16 * 1000) / 1000) ? false : isMaintainProportions;
            setIsMaintainProportions(!!isMaintainProportionsValue);

            if (playerId === EDITOR_PLAYER_ID) {
                (window as any).parent.postMessage(
                    {
                        type: 'set-maintain-proportion',
                        isMaintainProportions: isMaintainProportionsValue,
                    },
                    '*'
                );
            }
            if (playerMedia !== currentItem.id) {
                setTimeout(() => {
                    if (shouldChangeCurrentMedia) {
                        setCurrentMediaId(currentItem.id);
                        (window as any).GoBrain.widgets()[playerId].pause();
                        (window as any).GoBrain.widgets()[playerId].play();
                    }
                }, 100);
            } else {
                setTimeout(() => {
                    if (currentItem.playlistId) {
                        return;
                    }
                    const shouldPlay = isVisible && (isPlaying || changeSceneType === CHANGE_SCENE_TYPES.onclick);
                    shouldPlay && (window as any).GoBrain.widgets()[playerId].play();
                    shouldPlay && (window as any).GoBrain.widgets()[playerId].volume(currentVolumeLevel);
                }, 100);
            }
        }
    }, [turnOfChat, updateChatState]);

    const onPlayerPlayable = useCallback(() => {
        const { changeSceneType, mediaType, scenario, sceneConfig, props } = latestState.current;
        const { playerId, isVisible, settings, configParams, playerMedia } = props;

        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.playable,
            mediaId: playerMedia,
            scenarioId: scenario?.id,
            metadata: {},
            // Not props.configParams: the editor player is deliberately given an empty object,
            // and qplayerId belongs to the embed rather than to one player inside it.
            qplayerId: latestState.current.configParams.qplayerId
        });

        const autoplay = playerId === EDITOR_PLAYER_ID ? true : !isVisible ? false : isAllowAutoPlay({ configParams, settings });
        const defaultSceneBGMediaId = scenario?.defaultSceneBGMediaId ?? "";
        const isDefaultSceneBGMedia = playerMedia === defaultSceneBGMediaId && mediaType === 'scenario';
        if (!isVisible || ((mediaType === 'video' || isDefaultSceneBGMedia) && !autoplay)) {
            return;
        }

        const widget = (window as any).GoBrain.widgets()[playerId];
        widget?.play?.();
        const { defaults } = scenario ?? {};
        if (defaults?.sceneId && defaults?.sceneId === sceneConfig?.currentScene && changeSceneType === CHANGE_SCENE_TYPES.default) {
            if (!settings?.playbackOptions?.replay) {
                widget?.pause?.();
                setPlayerCurrentPosition(0);
                setIsPlaying(undefined);
            }
        }
    }, []);

    const onPlayerPlay = useCallback(() => {
        const { isPlaying, mediaName, accountId, isMediaLive, currentPosition, duration, isPosterRemoved, mediaType, scenario, props, configParams } = latestState.current;
        const { playerId, isVisible, playerMedia, settings, handlePlayerHover } = props;

        if (!isVisible) {
            return;
        }

        if (isPlaying === undefined || (isMediaLive && mediaType === 'scenario')) {
            if (isMediaLive && !LiveHelper.isAtLive(isMediaLive, currentPosition, duration)) {
                (window as any).GoBrain.widgets(playerId)?.position?.(LiveHelper.LIVE_POSITION_OFFSET);
            }

            setShowChatWindow(
                settings?.engagementFeatures?.enabled && settings?.engagementFeatures?.showChat && settings?.engagementFeatures?.openChatOnPlay
            );
            setShowMobileChat(
                settings?.engagementFeatures?.enabled && settings?.engagementFeatures?.showChat && settings?.engagementFeatures?.openChatOnPlay
            );

            handlePlayerHover?.();
        }
        setIsPlaying(true);

        MessageHelper.sendMessage({ type: MessageHelper.MESSAGE_TYPES.play, mediaId: playerMedia, scenarioId: scenario?.id, metadata: {}, qplayerId: configParams.qplayerId });
        window.parent.postMessage(
            {
                origin: 'Qbrick.Interact.Widget.Element',
                identifier: 'qbrick-player-analytics-message',
                command: {
                    type: 'AnalyticsEvent',
                    action: 'click',
                    data: 'play',
                    mediaName: mediaName,
                    mediaId: playerMedia,
                    accountId: accountId,
                    videoLength: duration,
                    isMediaLive: isMediaLive,
                    currentPosition: isMediaLive ? Date.now() : currentPosition,
                },
            },
            '*'
        );

        sendMediaGAPlayData(playerMedia, currentPosition);
        if (!isSendViewGAEvent) {
            sendMediaGAViewData(playerMedia, mediaName);
            setIsSendViewGAEvent(true);
        }

        !isPosterRemoved && setRemovePoster();
    }, [isSendViewGAEvent]);

    const onPlayerPause = useCallback(() => {
        const { currentPosition, mediaName, duration, isMediaLive, accountId, scenario, props, configParams } = latestState.current;
        const { isVisible, playerMedia } = props;

        if (!isVisible) {
            return;
        }

        setIsPlaying(false);
        MessageHelper.sendMessage({ type: MessageHelper.MESSAGE_TYPES.pause, mediaId: playerMedia, scenarioId: scenario?.id, metadata: {}, qplayerId: configParams.qplayerId });
        window.parent.postMessage(
            {
                origin: 'Qbrick.Interact.Widget.Element',
                identifier: 'qbrick-player-analytics-message',
                command: {
                    type: 'AnalyticsEvent',
                    action: 'click',
                    data: 'pause',
                    mediaName: mediaName,
                    mediaId: playerMedia,
                    accountId: accountId,
                    videoLength: duration,
                    isMediaLive: isMediaLive,
                    currentPosition: isMediaLive ? Date.now() : currentPosition,
                },
            },
            '*'
        );

        sendMediaGAPauseData(playerMedia, currentPosition);
    }, []);

    const onPlayerPlayComplete = useCallback(() => {
        const { mediasData, scenario, sceneConfig, props, currentPosition, currentSceneWidgets, configParams } = latestState.current;
        const { playerId, isVisible, playerMedia, onPlayComplete } = props;

        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.playComplete,
            mediaId: playerMedia,
            scenarioId: scenario?.id,
            metadata: {},
            qplayerId: configParams.qplayerId
        });

        // Only the clip on screen may act on its own end - the slides either side of it are mounted
        // too, and a stale playComplete from one of those would move the feed under the viewer.
        if (isVisible) {
            onPlayComplete?.();
        }

        if (!isVisible || playerId === EDITOR_PLAYER_ID || !sceneConfig?.nextScene) {
            return;
        }

        setChangeSceneType(CHANGE_SCENE_TYPES.default);
        if (sceneConfig?.nextScene !== sceneConfig?.currentScene) {
            if (sceneConfig?.currentScene) {
                updateSceneHistory(sceneConfig?.currentScene);
            }

            // The same media can belong to several scenes - prefer the entry of the scene being left.
            const playerMediaEntries = (mediasData ?? []).filter((m: any) => m.id === playerMedia);
            const currentSceneId = (playerMediaEntries.find((m: any) => m.sceneId === sceneConfig?.currentScene) ?? playerMediaEntries[0])?.sceneId;
            const currentSceneData = currentSceneId && (scenario?.scenes ?? []).find((s: any) => s.id === currentSceneId);

            let transitionOut = PLAYER_TRANSITIONS_OUT.fadeOut;
            if (currentSceneData?.metadata?.transition) {
                transitionOut = currentSceneData.metadata.transition.out ?? PLAYER_TRANSITIONS_OUT.fadeOut;
            }

            // Leaving the scene: every widget still on screen plays its exit first (see
            // InteractOverlay's groupExit), and the transition starts once the longest one ends.
            const exitDuration = getSceneEndExitDuration(currentSceneWidgets, typeof currentPosition === 'number' ? currentPosition : 0);
            if (exitDuration > 0) {
                setSceneEndExiting(true);
            }
            setTimeout(() => {
                // Make sure that transition layer should be trigger 1st. Then others scene change stuffs
                setTransitionOut(transitionOut);
                setTransitionLayer(true);
                setTimeout(() => {
                    setSceneConfig({ ...sceneConfig, currentScene: sceneConfig.nextScene });
                    // Reset the playhead as Go to scene does: the old scene's end position would
                    // otherwise pick the new scene's widgets before their start and play their entry.
                    setPlayerCurrentPosition(0);
                    setSceneEndExiting(false);
                }, 200);
            }, exitDuration);
        } else {
            const widget = (window as any).GoBrain.widgets()[playerId];
            widget?.position?.(0);
            widget?.play?.();
        }
    }, []);

    const onPlayerPositionChange = useCallback((position: number) => {
        const { isMediaLive, duration, scenario, currentPosition, props, isTransitionLayerEnable, startPlayerAt, configParams } = latestState.current;
        const { playerId, isVisible, playerMedia, onPicture } = props;

        if (!isVisible) {
            return;
        }

        // Past zero this player is definitely painting frames. Reported per player rather than
        // through the shared position, so a last event from the clip being scrolled away from
        // cannot be mistaken for the incoming one having picture.
        if (position > 0) {
            onPicture?.();
        }

        // Seek to the `in` start time once the media is actually playing - earlier seeks are dropped.
        // Retried a few times, since the first position events can still come from before the seek.
        if (pendingInTime.current === undefined) {
            const inTime = parseInt(configParams?.startAt, 10);
            // Without an `id`, only a single Player may take the start time: the story feed mounts one
            // Player per slide, all reading the same configParams.
            const isTargetMedia = configParams?.id
                ? configParams.id === playerMedia
                : !isStoryPlaylistConfig(latestState.current.playlistId, configParams);
            pendingInTime.current = inTime > 0 && isTargetMedia && !isMediaLive && latestState.current.mediaType !== 'scenario' ? inTime : null;
        }
        if (pendingInTime.current !== null) {
            const inTime = pendingInTime.current;
            if (Math.abs(position - inTime) < 1 || inTimeSeekAttempts.current >= 3) {
                pendingInTime.current = null;
            } else {
                inTimeSeekAttempts.current++;
                (window as any).GoBrain.widgets(playerId)?.position?.(inTime);
                setPlayerCurrentPosition(inTime, playerMedia);
                return;
            }
        }

        if (isTransitionLayerEnable && !hasTriggeredTransitionEffect.current) {
            hasTriggeredTransitionEffect.current = true;
            setTransitionEffectStatus(true);
        }

        // `latestLiveDuration` rather than `duration`: see the ref's declaration - the state copy
        // trails a render behind the position event it has to be added to.
        const realPos = isMediaLive ? (latestLiveDuration.current || duration) + position : position;
        // Tagged with the media: right after a scene switch the outgoing player can still pass the
        // isVisible check above and report its old position (see InteractOverlay).
        setPlayerCurrentPosition(realPos, playerMedia);

        if (typeof startPlayerAt === 'number' && startPlayerAt > 0 && startPlayerAt === currentPosition) {
            (window as any).GoBrain.widgets(playerId)?.position?.(startPlayerAt);
            setPlayerCurrentPosition(startPlayerAt);
            setTimeout(() => {
                setStartPlayerAt(null);
            }, 100);
        }

        if ((typeof currentPosition === 'number' && currentPosition <= 0) && checkpointRef.current) {
            checkpointRef.current = false;
            setTimeout(() => {
                isMediaLive && (window as any).GoBrain.widgets(playerId)?.position?.(LiveHelper.LIVE_POSITION_OFFSET);
            });
        }

        if (isMediaLive && liveStoppingTime && liveStoppingTime.current) {
            if (liveStoppingTime.current &&
                Math.ceil(NumberHelper.precision3(liveStoppingTime.current)) === Math.ceil(NumberHelper.precision3(realPos))) {
                setIsPlaying(undefined);
                liveStoppingTime.current = null;
            }
        }

        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.position,
            mediaId: playerMedia,
            scenarioId: scenario?.id,
            metadata: { position: position },
            qplayerId: configParams.qplayerId
        });

        if (positionMessage.current === 'no-notify') {
            return;
        }

        if (playerId === EDITOR_PLAYER_ID) {
            window.parent.postMessage(
                {
                    type: 'position',
                    position: realPos,
                },
                '*'
            );
        }
    }, []);

    const onPlayerDurationChange = useCallback((durationParam: any) => {
        const { mediaDurations, duration, scenario, props, configParams } = latestState.current;
        const { isVisible, playerMedia } = props;

        const newDuration = Math.abs(durationParam);
        latestLiveDuration.current = newDuration;
        const mediaDuration = {
            id: playerMedia,
            duration: newDuration,
        };
        if (!mediaDurations.find((m: any) => m.id === mediaDuration.id)) {
            setMediaWithDuration([...mediaDurations, mediaDuration]);
        }

        if (isVisible && newDuration !== duration) {
            setPlayerDuration(newDuration);
        }

        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.duration,
            mediaId: playerMedia,
            scenarioId: scenario?.id,
            metadata: { duration: durationParam },
            qplayerId: configParams.qplayerId
        });
    }, []);

    const onPlayerNotificationEvent = useCallback((notification: any) => {
        const { mediasData, isMediaLive, currentMediaId, props } = latestState.current;
        const { playerId, isVisible, settings, configParams } = props;

        if (!isVisible) {
            return;
        }

        const autoplay = playerId === EDITOR_PLAYER_ID ? true : !isVisible ? false : isAllowAutoPlay({ configParams, settings });
        if (notification.key === 'PlaybackItem.Track.Sequence.Cover.AttributesSet' && notification.data?.coverId) {
            const isLiveStarted = notification.data?.start === 0 && notification.data?.end === 0;
            const isLiveStopped = notification.data?.start !== 0 && notification.data?.end === null ? notification.data?.start : null;
            liveStoppingTime.current = isLiveStopped;

            setIsLiveStarted(isLiveStarted);
            setIsLiveStopped(isLiveStopped);
            if (isLiveStarted) {
                autoplay && setRemovePoster();
                !autoplay && setRemovePoster(false);
            }
        }

        if (notification.key === 'PlaybackItem.Track.Sequence.Cover.Deleted' && !autoplay && isMediaLive) {
            setRemovePoster(false);
        }

        if (notification.key === 'PlaybackItem.Track.Sequence.Slide.AttributesSet') {
            if (Object.prototype.hasOwnProperty.call(notification.data, 'start') && typeof notification?.data?.start === "number") {
                const slide = { ...notification.data, id: notification.data?.slideId ?? notification.data?.id };
                addPresentationPublishedSlide(slide);
            }
        }

        if (notification.key === 'PlaybackItem.Track.Sequence.Custom.AttributesSet') {
            if (Object.prototype.hasOwnProperty.call(notification.data, 'start') && typeof notification?.data?.start === "number" && !notification?.data?.end) {
                setCurrentInteractiveElement(notification.data);
            } else if (!notification?.data?.custom?.mediaId) {
                setCurrentInteractiveElement(null);
            }
        }

        if (notification.key === 'PlaybackItem.CustomPropertySet' && !!notification.data?.customProperty?.value) {
            const newCustom = JSON.parse(notification.data?.customProperty?.value) ?? {};
            const mediaId = notification.data.mediaId;
            const newMediasData = mediasData.map((m: any) => (m.id === mediaId || m.originalId === mediaId ? { ...m, custom: newCustom } : m));
            setMediasData(newMediasData);

            if (mediaId === currentMediaId) {
                updateChatState(newCustom);
            }
        }
    }, []);

    const onPlayerVolumeChange = useCallback((volumeLevel: number) => {
        const { scenario, props, configParams } = latestState.current;
        if (!props.isVisible) {
            return;
        }
        setCurrentVolumeLevel(volumeLevel);

        // Send message to communicate with embed sites
        MessageHelper.sendMessage({
            type: MessageHelper.MESSAGE_TYPES.volume,
            mediaId: props.playerMedia,
            scenarioId: scenario?.id,
            metadata: { volume: volumeLevel },
            qplayerId: configParams.qplayerId
        });

        // Send message to QVP to handle player mute/unmute
        const isPlayerMute = volumeLevel === 0;
        window.parent.postMessage(
            {
                origin: 'Qbrick.Interact.Widget.Element',
                identifier: 'qbrick-modal-handler-event',
                command: {
                    action: 'click',
                    data: isPlayerMute ? 'player-mute' : 'player-unMute',
                },
            },
            '*'
        );
    }, []);

    const onPlayerError = useCallback((error: any) => {
        const { props, configParams, restrictedMedias, errorDetails } = latestState.current;
        const { playerMedia } = props;
        const response = error?.data?.response ? JSON.parse(error?.data?.response) : {};
        if (PlayerHelper.NON_FATAL_ERROR_TYPES.includes(error?.type)) {
            return;
        }
        if (error.code === 403 && response.token === true) {
            createdPlayerInstance.current = false;
            const newRestrictedMedias = [...(restrictedMedias ?? []), playerMedia];
            MessageHelper.sendMessage({
                type: MessageHelper.MESSAGE_TYPES.mediaRestricted,
                mediaId: playerMedia,
                scenarioId: scenario?.id,
                metadata: {},
                qplayerId: configParams.qplayerId
            });
            setRestrictedMedias(newRestrictedMedias);
        } else {
            const newError = { details: error, code: error?.code, mediaId: playerMedia };
            const newErrorDetails = errorDetails.filter((error: any) => error.mediaId !== playerMedia).filter(Boolean);
            newErrorDetails.push(newError);
            setErrorDetails(newErrorDetails);
        }

        if (error.name === "Advertisement") {
            showControls();
        }
    }, [scenario?.id, setErrorDetails, setRestrictedMedias, showControls]);

    const playVisiblePlayer = useCallback(() => {
        if (!playerId) {
            return;
        }

        const widget = (window as any).GoBrain.widgets()[playerId];
        const { currentIndex, queue } = widget?.Queue?.get() ?? {};
        let currentMedia = mediasData.find((media: any) => media?.id === playerMedia || media?.originalId === playerMedia);

        if (!(currentMedia?.id && currentMedia?.mediaDetails) && queue?.[0]) {
            currentMedia = FetchDataHelper.getMediaData({
                mediaData: queue?.[0],
                id: queue?.[0]?.id ?? playerMedia,
                token
            });
        }

        const isCurrentMediaLive = currentMedia?.live;
        const isLiveStarted = !!currentMedia?.custom?.livemanager?.start;
        const isLiveStopped = isLiveStarted && !!currentMedia?.custom?.livemanager?.stop;

        if (playerId === EDITOR_PLAYER_ID) {
            return;
        }

        if (isVisible) {
            setIsLiveStarted(isLiveStarted);
            setIsLiveStopped(isLiveStopped);

            if (mediaType === 'scenario') {
                // Not show controls when live haven't started or is stopped
                (!isLiveStarted && !isLiveStopped) || isLiveStopped && setIsPlaying(undefined);
                isCurrentMediaLive !== isMediaLive && setIsMediaLive(isCurrentMediaLive); // Only set redux store isMediaLive if visible
            }

            if ((!isLiveStarted || isLiveStopped) && isCurrentMediaLive) {
                return;
            }

            setTimeout(() => {
                const { defaults } = scenario ?? {};
                if (defaults?.sceneId && defaults?.sceneId === sceneConfig?.currentScene && changeSceneType === CHANGE_SCENE_TYPES.default) {
                    settings?.playbackOptions?.replay && widget?.play?.();
                    if (!settings?.playbackOptions?.replay) {
                        setPlayerCurrentPosition(0);
                        setIsPlaying(undefined);
                        setRemovePoster(false)
                    }
                    setSceneHistory([]);
                } else {
                    isPlaying !== undefined && setIsPlaying(true);
                    widget?.play?.();
                }

                // (window as any).GoBrain.widgets()[playerId].on('queueItem', () => {}) onlly sent one time when player init
                // call handlePlayerQueueItem here to update player when changed
                if (mediaType === 'scenario') {
                    onPlayerQueueItem({ currentIndex, currentItem: queue?.[0] });
                }

                // The `in` start time is applied in onPlayerPositionChange: at this point the media
                // is not loaded yet and GoBrain drops the seek.
                widget?.volume?.(currentVolumeLevel);
            });
        } else if (!isVisible) {
            setTimeout(() => {
                if (!isCurrentMediaLive || isLiveStarted) {
                    widget?.pause?.();
                }
                widget?.position?.(0);
                widget?.volume?.(0);
            }, 200);
        }
    }, [changeSceneType, currentVolumeLevel, isMediaLive, isVisible, mediaType, mediasData, onPlayerQueueItem, playerId, playerMedia, scenario, sceneConfig?.currentScene, settings?.playbackOptions?.replay, token, isPlaying, isTransitionLayerEnable]);

    const onReceiveMessage = useCallback((event: MessageEvent) => {
        const { props, currentPosition } = latestState.current;
        const isFromQVPEditor = (event.origin.includes('video.qbrick.com') || event.origin.includes('localhost:9000'))
            && event.data.identifier === MessageHelper.MESSAGE_BASE_IDENTIFIERS.editorIdentifier;
        if (!isFromQVPEditor || !props.isVisible) {
            return;
        }

        if (event.data?.type === 'move-position-from-editor' && event.data?.position !== undefined && event.data?.position !== null) {
            positionMessage.current = event.data?.message;
        }
        if (event.data?.type === 'pause') {
            setIsPlaying(false);
        }
        if (event.data?.type === 'play') {
            setIsPlaying(true);
        }

        PlayerHelper.onReceiveMessage({ event, playerId: props.playerId, currentPosition, isVisible: !!props.isVisible });
    }, []);

    const handleEditorPlayer = useCallback(() => {
        if (playerId === EDITOR_PLAYER_ID) {
            const widget = (window as any).GoBrain.widgets()[playerId];
            const currentMedia = mediasData.find((media: any) => media?.id === playerMedia);
            if (!currentMedia?.start) {
                return;
            }
            widget?.position?.(currentMedia.start);
            setPlayerCurrentPosition(currentMedia.start);
        }
    }, [mediasData, playerId, playerMedia]);

    const sendMessageToParent = useCallback((type: string) => {
        const { props, scenario, configParams } = latestState.current;
        MessageHelper.sendMessage({ type: type, mediaId: props.playerMedia, scenarioId: scenario?.id, metadata: {}, qplayerId: configParams.qplayerId });
    }, []);

    const onPlayerSeek = useCallback(() => sendMessageToParent(MessageHelper.MESSAGE_TYPES.seek), [sendMessageToParent]);
    const onPlayerSeeked = useCallback(() => {
        setUserSeekedPlayer(true);
        setTimeout(() => {
            setUserSeekedPlayer(false);
        }, 200);
        sendMessageToParent(MessageHelper.MESSAGE_TYPES.seeked);
    }, [sendMessageToParent]);

    const adsPlay = useCallback((_event: any) => {
        hideControls();
    }, [hideControls]);

    const adsSkipped = useCallback((event: any) => {
        const { adPosition, totalAds } = event.adPodInfo?.data || {};
        if (adPosition === totalAds) {
            showControls();
        }
    }, [showControls]);

    const adsComplete = useCallback((event: any) => {
        const { adPosition, totalAds } = event.adPodInfo?.data || {};
        if (adPosition === totalAds) {
            showControls();
        }
    }, [showControls]);

    const playerEvents = {
        queueItem: onPlayerQueueItem,
        playable: onPlayerPlayable,
        play: onPlayerPlay,
        pause: onPlayerPause,
        playComplete: onPlayerPlayComplete,
        position: onPlayerPositionChange,
        duration: onPlayerDurationChange,
        notification: onPlayerNotificationEvent,
        volume: onPlayerVolumeChange,
        error: onPlayerError,
        seek: onPlayerSeek,
        seeked: onPlayerSeeked,
        adsPlay: adsPlay,
        adsSkipped: adsSkipped,
        adsComplete: adsComplete,
    };

    const createPlayerInstance = useCallback(() => {
        const { isMediaLive, mediasData, playlistId, scenario, mediaType, props, configParams } = latestState.current;
        const currentMedia = mediasData.find((media: any) => media?.id === props.playerMedia);
        const isStoryPlaylist = isStoryPlaylistConfig(playlistId, configParams);
        const playerProps = { ...props, configParams, playlistId, isMediaLive: currentMedia?.live || isMediaLive, isStoryPlaylist };

        const handlePlayerReady = (value: boolean) => {
            createdPlayerInstance.current = true;
            onPlayerReady(value);
            MessageHelper.sendMessage({
                type: MessageHelper.MESSAGE_TYPES.ready,
                mediaId: props.playerMedia,
                scenarioId: scenario?.id,
                metadata: {},
                qplayerId: configParams.qplayerId
            });
            PlayerHelper.subscribePlayerEvents(props.playerId, playerEvents);
        }
        PlayerHelper.createPlayerInstance({ playerProps, scenario, onPlayerReady: handlePlayerReady, mediaType });
    }, [onPlayerReady]);

    const initializePlayer = useCallback(() => {
        if (createdPlayerInstance.current) {
            return;
        };
        if (accountId && (playerMedia || playlistId)) {
            createPlayerInstance();
            !playlistId && playVisiblePlayer();
        }
        (window as any).addEventListener('message', onReceiveMessage);

        return () => (window as any).removeEventListener('message', onReceiveMessage);
    }, [accountId, createPlayerInstance, onReceiveMessage, playerMedia, playlistId]);

    useEffect(() => {
        return () => {
            PlayerHelper.unsubscribePlayerEvents(playerId, playerEvents);
            const widgets = (window as any).GoBrain.widgets();
            widgets[playerId]?.destroy?.();
            try { delete widgets[playerId]; } catch (_e: unknown) { /* registry may be non-configurable */ }
        };
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (showCountdownModule && createdPlayerInstance.current) {
            return;
        }

        if (isVisible) {
            const defaultVolume = settings?.playbackOptions?.setDefaultVolume ? settings?.playbackOptions?.defaultVolumeValue / 100 || 0.5 : 0.5;
            const volume =
                playerId === EDITOR_PLAYER_ID
                    ? 0
                    : !configParams?.mute
                        ? settings?.playbackOptions?.muteAtStart
                            ? 0
                            : defaultVolume
                        : configParams?.mute === 'true'
                            ? 0
                            : defaultVolume;
            setCurrentVolumeLevel(volume);
        }

        initializePlayer();
    }, [token, showCountdownModule]);

    useEffect(() => {
        if (playerId === EDITOR_PLAYER_ID) {
            setLoading(false);
            handleEditorPlayer();
        } else {
            PlayerHelper.setThumbnailForPlayer(thumbnailUrl, (loading: boolean) => setLoading(loading));
        }
    }, [handleEditorPlayer, playerId, thumbnailUrl]);

    useEffect(() => {
        playVisiblePlayer();
    }, [isVisible]);

    return (
        <div
            style={{ visibility: loading ? 'hidden' : 'visible' }}
            id={`${playerId}-container`}
            className={joinClassNames(
                'main-player',
                isVisible && !showCountdownModule ? 'main-player--visible' : 'main-player--hidden',
                isMaintainProportions !== false && configParams?.fromQplayerWidget !== "true" ? 'qplayer--maintain-proportions' : '',
                minimizedPlayer ? 'qplayer--minimized' : '',
                classNames
            )}
            onTouchEnd={handlePlayerHover}
            aria-hidden={!!showCountdownModule}
        />
    );
}

export default Player;